import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { ThemeProvider } from 'react-jss';

import { useFullscreen } from 'react-use';
import { TourProvider } from '@reactour/tour';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

import ThemeContext from '../contexts/themeContext';
import Wrapper from '../layout/Wrapper/Wrapper';

import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { dynamicLoader, getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import AsideRoutes from '../layout/Aside/AsideRoutes';
import auth from '../shared/mobx/auth-state';
import { useLocation } from 'react-router-dom'; // Import useLocation
import { scrollToHashAnchor } from '../helpers/scrollToAnchor';


const App = () => {

	const { hash } = useLocation();

	useEffect(() => {
		if (!hash) {
			return;
		}
		const t = window.setTimeout(() => {
			scrollToHashAnchor(hash);
		}, 0);
		return () => window.clearTimeout(t);
	}, [hash]);
	dynamicLoader.removeDynamicElements();
	getOS();

	dayjs.extend(localizedFormat);
	dayjs.extend(relativeTime);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};
	const location = useLocation(); // Get the current route
	
	useEffect(() => {
		if (location.pathname === '/') {
            document.body.classList.add('home-page');
        }else{
            document.body.classList.remove('home-page');
        }
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
			document.documentElement.setAttribute('data-bs-theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
			document.documentElement.removeAttribute('data-bs-theme');
		};
	}, [darkModeStatus,location.pathname]);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */

	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	return (
		<ThemeProvider theme={theme}>
			<TourProvider steps={steps} styles={styles} showNavigation={false} showBadge={false}>
				<div
					ref={ref}
					className='app'
					style={{
						backgroundColor: fullScreenStatus ? 'var(--bs-body-bg)' : undefined,
						zIndex: fullScreenStatus ? 1 : undefined,
						overflow: fullScreenStatus ? 'scroll' : undefined,
					}}>
					{
						auth.isLoggedIn() && <AsideRoutes />
					}
					<Wrapper />
				</div>
				{/* <Portal id='portal-notification'>
					<ReactNotifications />
				</Portal>
				<ToastContainer closeButton={ToastCloseButton} toastClassName='toast show' />
				<NotificationToast/> */}
			</TourProvider>
		</ThemeProvider>
	);
};

export default App;
